import { useEffect, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { formatDuration, formatMeters } from "../../lib/format";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { ResultResponse } from "../../types/routecore";

// Resultado consolidado do job. Só disponível quando o job atinge
// estado terminal com solução (solved ou partial).
type Props = {
  onNavigate: (tabId: string) => void;
};

export function TabJobResult({ onNavigate }: Props) {
  const { trackedJobId, setTrackedJobId } = useAppState();
  const [input, setInput] = useState(trackedJobId ?? "");
  const [result, setResult] = useState<APIResult<ResultResponse> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(trackedJobId ?? "");
  }, [trackedJobId]);

  async function fetchResult() {
    if (!input) return;
    setLoading(true);
    setResult(null);
    const r = await api.getJobResult(input.trim());
    setResult(r as APIResult<ResultResponse>);
    setLoading(false);
    setTrackedJobId(input.trim());
  }

  const data = result?.data;
  const summary = data?.summary;
  const metrics = data?.solver_metrics;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Resultado do job"
        subtitle="Estrutura completa com rotas, paradas, ETAs, custos, GeoJSON e métricas do solver."
        endpoint={{ method: "GET", path: "/v1/route-optimization/jobs/:id/result" }}
        actions={
          <button
            type="button"
            className="ks-btn-secondary text-xs"
            onClick={() => onNavigate("map")}
            disabled={!data}
          >
            ver no mapa →
          </button>
        }
      />

      <SectionCard
        title="Job"
        actions={
          <button
            type="button"
            className="ks-btn-primary text-xs"
            disabled={loading || !input}
            onClick={fetchResult}
          >
            {loading ? "buscando..." : "Buscar resultado"}
          </button>
        }
      >
        <input
          type="text"
          className="ks-input ks-code"
          placeholder="job_id (UUID)"
          value={input}
          onChange={(e) => setInput(e.target.value.trim())}
        />
      </SectionCard>

      {summary && (
        <SectionCard title="Resumo da solução">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Rotas" value={summary.total_routes.toString()} />
            <Stat label="Pedidos alocados" value={summary.assigned_orders.toString()} />
            <Stat label="Não alocados" value={summary.unassigned_orders.toString()} tone={summary.unassigned_orders ? "warn" : "ok"} />
            <Stat label="Distância total" value={formatMeters(summary.total_distance_meters)} />
            <Stat label="Duração total" value={formatDuration(summary.total_duration_seconds)} />
            <Stat label="Custo total" value={`R$ ${summary.total_cost}`} />
          </div>
        </SectionCard>
      )}

      {metrics && (
        <SectionCard title="Métricas do solver">
          <dl className="grid gap-2 text-sm sm:grid-cols-4">
            <KV label="Engine" value={metrics.solver_engine} />
            <KV label="Estratégia" value={metrics.strategy} />
            <KV label="Tempo de processamento" value={`${metrics.processing_time_seconds.toFixed(2)} s`} />
            <KV label="Iterações" value={metrics.iterations.toLocaleString("pt-BR")} />
            {metrics.gap !== undefined && (
              <KV label="Gap" value={`${(metrics.gap * 100).toFixed(2)}%`} />
            )}
          </dl>
        </SectionCard>
      )}

      {data && data.routes.length > 0 && (
        <SectionCard title={`Rotas (${data.routes.length})`}>
          <div className="ks-scroll max-h-[400px] space-y-2 overflow-auto pr-2">
            {data.routes.map((r) => (
              <details
                key={r.route_id}
                className="rounded-md border border-ink-200 bg-white"
              >
                <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
                  <span className="font-semibold text-ink-900">{r.driver_external_id}</span>
                  <span className="text-ink-500">·</span>
                  <span className="text-ink-700">{r.vehicle_external_id}</span>
                  <span className="ml-auto text-xs text-ink-600">
                    {r.stops.length} paradas · {formatMeters(r.total_distance_meters)} ·{" "}
                    {formatDuration(r.total_duration_seconds)}
                  </span>
                </summary>
                <ul className="border-t border-ink-200">
                  {r.stops.map((s) => (
                    <li
                      key={s.sequence}
                      className="flex items-center gap-3 border-b border-ink-100 px-3 py-1.5 text-[12.5px] last:border-0"
                    >
                      <span className="inline-flex size-5 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">
                        {s.sequence}
                      </span>
                      <span className="font-mono text-ink-700">{s.type}</span>
                      {s.order_id && (
                        <span className="font-mono text-ink-900">{s.order_id}</span>
                      )}
                      <span className="ml-auto text-ink-500">
                        ETA {new Date(s.eta).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </SectionCard>
      )}

      {data && data.unassigned_orders.length > 0 && (
        <SectionCard title={`Pedidos não alocados (${data.unassigned_orders.length})`}>
          <ul className="space-y-1">
            {data.unassigned_orders.map((u) => (
              <li
                key={u.order_external_id}
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              >
                <p className="font-mono">{u.order_external_id}</p>
                <p className="text-[11px] uppercase tracking-wide">{u.reason}</p>
                {u.detail && <p>{u.detail}</p>}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <ResponseCard
        result={result}
        loading={loading}
        emptyHint="Quando o job atingir estado terminal com solução, o resultado completo aparece aqui."
      />
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn";
}) {
  const cls =
    tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-ink-200 bg-white text-ink-800";
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-base font-semibold">{value}</p>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-ink-200 px-3 py-2">
      <dt className="ks-label">{label}</dt>
      <dd className="text-sm text-ink-800">{value}</dd>
    </div>
  );
}
